import { HStack, Icon } from '@chakra-ui/react';
import { ja } from 'date-fns/locale';
import React from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { FiX } from 'react-icons/fi';

import { TypeUtils } from '../../framework/utils/UtilityTypes';
import { Formatter } from '../utils/FormatUtils';
import StyledButton from './StyledButton';

/**
 * クリアボタン付き datepicker
 */
export type DatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  /**
   * react-datepicker プロパティ
   */
  datePickerProps?: Partial<ReactDatePickerProps>;
  /**
   * Controller.render プロパティ
   */
  field: ControllerRenderProps<TFieldValues, TName>;
  /**
   * クリアボタンイベント
   */
  onClickClear: Runnable;
};

type DatePickerType = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: DatePickerProps<TFieldValues, TName>
) => JSX.Element;

/**
 * クリアボタン付き datepicker
 *
 * Reference: https://stackoverflow.com/questions/67420624/disable-keyboard-on-mobile-device-for-react-datepicker
 *
 * @returns
 */
const DatePicker = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  datePickerProps,
  field,
  ...props
}: DatePickerProps<TFieldValues, TName>) => {
  const dateFormat = React.useMemo<ReactDatePickerProps['dateFormat']>(
    () =>
      datePickerProps?.showTimeInput || datePickerProps?.showTimeSelect
        ? Formatter.format.DATETIME
        : Formatter.format.DATE,
    [datePickerProps?.showTimeInput, datePickerProps?.showTimeSelect]
  );
  const onChange = React.useCallback<ReactDatePickerProps['onChange']>(
    (date, event) => {
      field.onChange(date, event);
      if (!TypeUtils.isNil(datePickerProps?.onChange)) {
        datePickerProps?.onChange(date, event);
      }
    },
    []
  );
  const onFocus = React.useCallback<
    Consumer<React.FocusEvent<HTMLInputElement, Element>>
  >((event) => event.target.blur(), []);

  return (
    <HStack justifyContent="start">
      <ReactDatePicker
        dateFormat={dateFormat}
        locale={ja}
        selected={field.value ? new Date(field.value.toString()) : undefined}
        timeIntervals={5}
        {...datePickerProps}
        {...field}
        disabledKeyboardNavigation
        onChange={onChange}
        onFocus={onFocus}
        withPortal
      />
      <StyledButton colorScheme="blackAlpha">
        <Icon as={FiX} onClick={props.onClickClear} />
      </StyledButton>
    </HStack>
  );
};

export default React.memo(DatePicker) as DatePickerType;
